import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

export function AdminLogin() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [recoveryKey, setRecoveryKey] = useState('')
  const [error, setError] = useState('')
  const [showPasswordFallback, setShowPasswordFallback] = useState(false)
  const [showRecovery, setShowRecovery] = useState(false)
  
  const { data: status, isLoading: statusLoading, refetch } = useQuery({
    queryKey: ['admin', 'setup-status'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/setup-status`, { credentials: 'include' })
      return res.json()
    },
  })
  
  const isSetup = status?.isSetup
  const hasPasskey = status?.hasPasskey
  
  // Auto-trigger passkey login if available
  useEffect(() => {
    if (hasPasskey && !showPasswordFallback && !showRecovery) {
      handlePasskeyLogin()
    }
  }, [hasPasskey])
  
  // Passkey registration (first-time setup - no secret needed)
  const registerPasskey = async () => {
    setError('')
    
    try {
      // Get registration options
      const optionsRes = await fetch(`${apiUrl}/admin/passkey/register-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      })
      const options = await optionsRes.json()
      
      if (options.error) {
        throw new Error(options.error)
      }
      
      // Convert challenge from base64url
      options.challenge = base64UrlDecode(options.challenge)
      options.user.id = base64UrlDecode(options.user.id)
      
      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: options,
      }) as PublicKeyCredential
      
      if (!credential) {
        throw new Error('Failed to create credential')
      }
      
      const response = credential.response as AuthenticatorAttestationResponse
      
      // Send to server
      const registerRes = await fetch(`${apiUrl}/admin/passkey/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            response: {
              attestationObject: arrayBufferToBase64Url(response.attestationObject),
              clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
            },
            type: credential.type,
          },
        }),
      })
      
      const result = await registerRes.json()
      if (result.error) {
        throw new Error(result.error)
      }
      
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Failed to register passkey')
    }
  }
  
  // Passkey login
  const handlePasskeyLogin = async () => {
    setError('')
    try {
      // Get login options
      const optionsRes = await fetch(`${apiUrl}/admin/passkey/login-options`, {
        method: 'POST',
        credentials: 'include',
      })
      const options = await optionsRes.json()
      
      if (options.error) {
        setShowPasswordFallback(true)
        return
      }
      
      // Convert from base64url
      options.challenge = base64UrlDecode(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred: any) => ({
          ...cred,
          id: base64UrlDecode(cred.id),
        }))
      }
      
      // Get credential
      const credential = await navigator.credentials.get({
        publicKey: options,
      }) as PublicKeyCredential
      
      if (!credential) {
        throw new Error('Authentication cancelled')
      }
      
      const response = credential.response as AuthenticatorAssertionResponse
      
      // Send to server
      const loginRes = await fetch(`${apiUrl}/admin/passkey/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            response: {
              authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
              clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
              signature: arrayBufferToBase64Url(response.signature),
            },
            type: credential.type,
          },
        }),
      })
      
      const result = await loginRes.json()
      if (result.error) {
        throw new Error(result.error)
      }
      
      navigate('/')
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setShowPasswordFallback(true)
      } else {
        setError(err.message || 'Failed to authenticate')
      }
    }
  }
  
  // Password login
  const loginMutation = useMutation({
    mutationFn: async (password: string) => {
      const res = await fetch(`${apiUrl}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Login failed')
      return data
    },
    onSuccess: () => navigate('/'),
    onError: (err: Error) => setError(err.message),
  })
  
  // Recovery
  const recoverMutation = useMutation({
    mutationFn: async (recoveryKey: string) => {
      const res = await fetch(`${apiUrl}/admin/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recoveryKey }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Recovery failed')
      return data
    },
    onSuccess: () => {
      setShowRecovery(false)
      setRecoveryKey('')
      refetch() // Refresh status to show setup screen
    },
    onError: (err: Error) => setError(err.message),
  })
  
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    loginMutation.mutate(password)
  }
  
  const handleRecoverySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    recoverMutation.mutate(recoveryKey)
  }
  
  if (statusLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-gradient mb-2">n0n3</h1>
          <p className="text-gray-400">
            {showRecovery ? 'Account Recovery' : !isSetup ? 'Admin Setup' : 'Admin Login'}
          </p>
        </div>
        
        <div className="card space-y-6">
          {/* Recovery Mode */}
          {showRecovery && (
            <form onSubmit={handleRecoverySubmit} className="space-y-6">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                <p className="text-yellow-400 font-medium mb-1">Account Recovery</p>
                <p className="text-gray-400">
                  Enter your recovery key to reset admin access.
                </p>
              </div>
              
              <div>
                <label htmlFor="recoveryKey" className="block text-sm font-medium text-gray-300 mb-2">
                  Recovery Key
                </label>
                <input
                  id="recoveryKey"
                  type="password"
                  value={recoveryKey}
                  onChange={(e) => setRecoveryKey(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500 text-white"
                  placeholder="Enter recovery key"
                  required
                />
              </div>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={recoverMutation.isPending}
                className="w-full btn btn-primary py-3"
              >
                {recoverMutation.isPending ? 'Recovering...' : 'Reset Admin Access'}
              </button>
              
              <button
                type="button"
                onClick={() => { setShowRecovery(false); setError('') }}
                className="w-full text-gray-500 hover:text-gray-300 text-sm"
              >
                Cancel
              </button>
            </form>
          )}
          
          {/* Setup - Register Passkey (First Time) */}
          {!showRecovery && !isSetup && (
            <>
              <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4 text-sm">
                <p className="text-primary-400 font-medium mb-1">First Time Setup</p>
                <p className="text-gray-400">
                  Create your passkey to secure the admin panel. Uses Face ID, Touch ID, or Windows Hello.
                </p>
              </div>
              
              <button
                onClick={registerPasskey}
                className="w-full btn bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-500 hover:to-purple-500 py-4 text-lg"
              >
                <span className="mr-2">🔐</span>
                Create Passkey
              </button>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
          
          {/* Login with Passkey */}
          {!showRecovery && isSetup && hasPasskey && !showPasswordFallback && (
            <>
              <div className="text-center py-6">
                <div className="text-6xl mb-4">🔐</div>
                <p className="text-gray-400 mb-6">
                  Use your passkey to sign in
                </p>
                <button
                  onClick={handlePasskeyLogin}
                  className="btn bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-500 hover:to-purple-500 py-3 px-8"
                >
                  Authenticate with Passkey
                </button>
              </div>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div className="text-center space-y-2">
                <button 
                  onClick={() => setShowPasswordFallback(true)}
                  className="text-sm text-gray-500 hover:text-gray-300 block w-full"
                >
                  Use password instead
                </button>
                <button 
                  onClick={() => { setShowRecovery(true); setError('') }}
                  className="text-sm text-gray-600 hover:text-gray-400 block w-full"
                >
                  Lost access? Recover account
                </button>
              </div>
            </>
          )}
          
          {/* Password Fallback */}
          {!showRecovery && isSetup && (!hasPasskey || showPasswordFallback) && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500 text-white"
                  placeholder="Enter password"
                  required
                />
              </div>
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loginMutation.isPending}
                className="w-full btn btn-primary py-3 disabled:opacity-50"
              >
                {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
              </button>
              
              <div className="text-center space-y-2">
                {hasPasskey && (
                  <button 
                    type="button"
                    onClick={() => {
                      setShowPasswordFallback(false)
                      handlePasskeyLogin()
                    }}
                    className="text-sm text-gray-500 hover:text-gray-300 block w-full"
                  >
                    Use passkey instead
                  </button>
                )}
                <button 
                  type="button"
                  onClick={() => { setShowRecovery(true); setError('') }}
                  className="text-sm text-gray-600 hover:text-gray-400 block w-full"
                >
                  Lost access? Recover account
                </button>
              </div>
            </form>
          )}
        </div>
        
        <p className="text-center text-gray-500 text-sm mt-6">
          <a href="https://n0n3.com" className="hover:text-gray-300">← Back to site</a>
        </p>
      </div>
    </div>
  )
}

// Helper functions
function base64UrlDecode(str: string): ArrayBuffer {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - base64.length % 4) % 4)
  const binary = atob(base64 + padding)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}
